"use client";

import { useState } from "react";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAddToCart } from "@/hooks/useAddToCart";
import { isPurchasable, variantPriceRange } from "@/lib/product-pricing";
import { formatPrice } from "@/lib/format-price";
import {
  VariantSelector,
  resolveVariantPrice,
  type VariantOption,
} from "../VariantSelector";
import { MysterySizeGuide } from "./MysterySizeGuide";
import {
  isMysteryFreshProduct,
  type MysterySizeGuideConfig,
} from "@/lib/mystery-colour-schemes";

interface ProductDetailAddToCartProps {
  productId: string;
  title: string;
  imageKey: string;
  pricePence: number | null;
  category: string;
  deliveryTiming: string;
  /** Product variants (sizes/options) — server-provided so the selector
   *  renders in the SSR HTML with no fetch. Empty for products without any. */
  variants?: VariantOption[];
  /** Add-on flag — snapshotted onto the cart line so the cart can label it. */
  isAddon?: boolean;
  /**
   * Manual stock flag (`products.inStock`). `< 1` means the admin marked the
   * product sold out: the buy box must refuse the add and say "Sold out",
   * exactly like the quick-view drawer (`ProductDetail.tsx`). Omitted/null is
   * treated as in stock so existing callers are unaffected.
   */
  inStock?: number | null;
  /**
   * Admin mystery size-guide config, resolved server-side by `/p/[id]` (which
   * already reads the whole settings object). Forwarded to `MysterySizeGuide` so
   * it does not re-fetch `/api/products/settings` for data the page had. Plain
   * serialisable data only — never the settings module (see MysterySizeGuide).
   */
  mysterySizeGuide?: MysterySizeGuideConfig | null;
}

/**
 * Add-to-cart button for the canonical /p/[id] product detail page.
 * Mirrors the cart-write semantics of the in-catalog drawer (`addItem`
 * with the storefront-facing post-surcharge price), so a customer
 * landing from Google Shopping gets the same item shape in the cart as
 * one navigating via the catalog. When the product has variants, a
 * selector is shown and the chosen variant (label + its price) is what
 * gets added. Enquire-only products skip the cart and route to the
 * enquiry form.
 */
export function ProductDetailAddToCart({
  productId,
  title,
  imageKey,
  pricePence,
  category,
  deliveryTiming,
  variants = [],
  isAddon = false,
  inStock = null,
  mysterySizeGuide = null,
}: ProductDetailAddToCartProps) {
  // `useAddToCart` writes the cart AND fires GA4's add_to_cart from one call.
  // This buy box was the one copy of the paste that omitted the tracking, so
  // every PDP add — the highest-intent surface on the site — went unrecorded.
  const addToCart = useAddToCart();
  const [adding, setAdding] = useState(false);
  const [quantity, setQuantity] = useState(1);
  // Start with NOTHING selected: never silently default to the first option, or
  // the order records a variant the customer never actually chose (e.g. "Male").
  // They must pick deliberately.
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(() => {
    // A lone option isn't a real choice — pre-select it. With 2+ options the
    // customer must pick deliberately (no silent default to the first).
    if (variants.length === 1) return variants[0]!.id;
    return null;
  });

  // Enquire-only OR nothing priceable (no base price and no priced variant).
  if (!isPurchasable(deliveryTiming, pricePence, variants)) {
    return (
      <Button asChild size="lg" className="w-full sm:w-auto">
        <a href="/enquire">
          <ShoppingBag className="mr-2 h-4 w-4" />
          Enquire about this piece
        </a>
      </Button>
    );
  }

  const selectedVariant =
    variants.find((v) => v.id === selectedVariantId) ?? null;
  const price = resolveVariantPrice(selectedVariant, pricePence);
  // Has options but none chosen yet — block add-to-cart until the customer
  // picks, so the order can never record an un-chosen (defaulted) variant.
  const needsSelection = variants.length > 0 && !selectedVariant;
  // Manual stock flag. Mirrors the quick-view drawer exactly
  // (`ProductDetail.tsx`: `maxQty = Math.max(1, product.inStock)` and
  // `disabled={product.inStock < 1 …}` / "Sold out"), so the canonical PDP —
  // the Merchant Center landing page — can never sell what the JSON-LD is
  // simultaneously reporting as OutOfStock.
  const stock = inStock ?? 1;
  const soldOut = stock < 1;
  const maxQty = Math.max(1, stock);
  // Belt-and-braces against a stale client: the stepper already clamps, but a
  // stock edit can land after this component mounted with a higher quantity.
  const safeQuantity = Math.min(Math.max(1, quantity), maxQty);

  const handleAdd = () => {
    // no sold-out / un-chosen / unpriced adds
    if (soldOut || needsSelection || price == null) return;
    setAdding(true);
    try {
      addToCart({
        productId,
        title,
        imageKey,
        // No `imageUrlRaw`: this handler only ever runs in the BROWSER, where
        // `R2_PUBLIC_URL` does not exist (it is a Worker var, not
        // `NEXT_PUBLIC_*`), so `getR2ImageUrl(imageKey)` returned "" on every
        // single add — while dragging `@opennextjs/cloudflare` into the PDP
        // client bundle to do it. Every consumer resolves the thumbnail with
        // `if (rawUrl) …` → `/api/gallery/image?key=…` (`CartSheet.mediaUrl`,
        // `CheckoutItemImage.mediaUrl`), and "" is falsy, so omitting the field
        // is byte-identical behaviour with none of the weight.
        priceSnapshot: price,
        category,
        deliveryTiming,
        isAddon,
        quantity: safeQuantity,
        ...(selectedVariant
          ? {
              variantId: selectedVariant.id,
              variantLabel: selectedVariant.label,
            }
          : {}),
      });
      toast.success(`${title} added to cart`);
    } finally {
      // Tiny debounce to prevent double-tap rage-clicks; the button text
      // returns to normal almost immediately so the UX still feels snappy.
      setTimeout(() => setAdding(false), 400);
    }
  };

  // Quantity stepper — same clamp + aria labels as the quick-view drawer.
  // Hidden while sold out (nothing to buy) and until an option is chosen
  // (picking a quantity before a variant is meaningless).
  const quantityStepper =
    soldOut || needsSelection ? null : (
      <div className="flex shrink-0 items-center gap-0 rounded-md border border-border bg-background">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="min-h-11 min-w-11"
          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
          aria-label="Decrease quantity"
        >
          −
        </Button>
        <Input
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min={1}
          max={maxQty}
          value={safeQuantity}
          aria-label="Quantity"
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!Number.isNaN(v)) setQuantity(Math.min(maxQty, Math.max(1, v)));
          }}
          className="min-h-11 w-12 border-0 bg-transparent p-0 text-center text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="min-h-11 min-w-11"
          onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
          aria-label="Increase quantity"
        >
          +
        </Button>
      </div>
    );

  const addButton = (
    <>
      <div className="flex w-full items-center gap-3">
        {quantityStepper}
        <Button
          size="lg"
          onClick={handleAdd}
          disabled={soldOut || adding || needsSelection || price == null}
          className="min-w-0 flex-1"
        >
          <ShoppingBag className="mr-2 h-4 w-4" />
          {soldOut
            ? "Sold out"
            : adding
              ? "Added!"
              : needsSelection
                ? "Choose an option"
                : "Add to cart"}
        </Button>
      </div>
      {/* Announce the add to screen readers — the button's own label change
          isn't reliably announced across ATs. */}
      <span role="status" aria-live="polite" className="sr-only">
        {adding ? "Added to cart" : ""}
      </span>
    </>
  );

  if (variants.length === 0) return addButton;

  // Reactive price: before a pick, show "From <lowest>" (only when options
  // actually differ in price); once an option is chosen, show that option's
  // exact price. Mirrors the quick-view modal so the two surfaces agree.
  const { min: rangeMin, max: rangeMax } = variantPriceRange(pricePence, variants);
  const hasRange = rangeMin != null && rangeMax != null && rangeMin !== rangeMax;
  const displayPrice = selectedVariant ? price : rangeMin ?? pricePence;
  const showFrom = needsSelection && hasRange;
  const showSizeGuide = isMysteryFreshProduct(category, title);

  return (
    <div className="flex w-full flex-col gap-4">
      {displayPrice != null && (
        <p className="flex items-baseline gap-1.5 font-serif font-semibold text-foreground">
          {showFrom && (
            <span className="text-lg font-medium text-muted-foreground">From</span>
          )}
          <span className="text-3xl leading-none sm:text-4xl">
            {formatPrice(displayPrice)}
          </span>
        </p>
      )}
      {showSizeGuide && (
        <MysterySizeGuide
          currentProductId={productId}
          config={mysterySizeGuide}
          className="w-full justify-center sm:w-auto sm:justify-start"
        />
      )}
      <VariantSelector
        variants={variants}
        basePricePence={pricePence}
        selectedId={selectedVariantId}
        onSelect={setSelectedVariantId}
        label="Choose an option"
        layout="stacked"
      />
      {addButton}
    </div>
  );
}
